import React, { useState } from "react";
import { IconArrowLeft, IconLock } from "@tabler/icons-react";
import {
  Anchor,
  Box,
  Button,
  Center,
  Group,
  PasswordInput,
  Stack,
  Text,
  Title,
} from "@mantine/core";

import { useForm } from "@mantine/form";

import "./styles.scss";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AppName, Colors, LogoFullWhite } from "../../Lib/Data";
import { Endpoints } from "../../Lib/Endpoints";
import { PerformRequest } from "../../Lib/usePerformRequest";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
} from "../../Lib/Methods";
import { motion } from "framer-motion";

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const { verificationToken } = useParams();

  document.title = `Reset Password | ${AppName}`;

  const passwordForm = useForm({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validate: {
      password: (value) =>
        value && value.length >= 6 ? null : "At LEAST 6 characters",
      confirmPassword: (value, values) =>
        value && value === values.password ? null : "Passwords do not match!",
    },
  });

  const [isLoading, setLoading] = useState<boolean>(false);

  const UpdatePassword = async () => {
    const { hasErrors } = passwordForm.validate();
    if (!hasErrors) {
      setLoading(true);
      const { password } = passwordForm.values;
      const r = await PerformRequest({
        method: "PATCH",
        route: Endpoints.GetUpdateUserPassword(),
        data: {
          password,
          token: verificationToken,
        },
        callback: () => {
          setLoading(false);
        },
      });
      setLoading(false);
      if (r) {
        if (r.status === 200) {
          localStorage.removeItem("t");
          localStorage.removeItem("a");
          DefaultSuccessNotification("Password updated!");
          navigate("/login");
        }
        if (r.status === 401) {
          DefaultErrorNotification("Invalid token!");
        }
      }
    }
  };

  return (
    <div className="reset-password-wrapper">
      {/* ─── Brand Panel (Left) ─── */}
      <motion.div
        className="reset-brand-panel"
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <div className="brand-content">
          <img src={LogoFullWhite} alt={AppName} className="brand-logo" />
          <motion.h2
            className="brand-headline"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            Almost there.
            <br />
            Set your new password.
          </motion.h2>
          <motion.p
            className="brand-subtitle"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            Choose a strong password to keep your account secure. You'll be redirected to login once done.
          </motion.p>
        </div>
      </motion.div>

      {/* ─── Form Panel (Right) ─── */}
      <div className="reset-form-panel">
        <motion.div
          className="reset-form-inner"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Stack gap={2} mb={32}>
            <Title order={2} fw={800} size={26}>
              New Password
            </Title>
            <Text c="dimmed" size="sm" fw={400}>
              Choose a secure password for your account
            </Text>
          </Stack>

          <Stack gap="sm">
            <PasswordInput
              label="Password"
              placeholder="Minimum 6 characters"
              size="sm"
              leftSection={<IconLock size={15} stroke={1.8} />}
              {...passwordForm.getInputProps("password")}
              classNames={{ input: "auth-input" }}
            />
            <PasswordInput
              label="Confirm Password"
              placeholder="Re-enter your password"
              size="sm"
              leftSection={<IconLock size={15} stroke={1.8} />}
              {...passwordForm.getInputProps("confirmPassword")}
              classNames={{ input: "auth-input" }}
            />

            <Button
              fullWidth
              mt="sm"
              onClick={UpdatePassword}
              loading={isLoading}
              color={Colors.GreenPrimary}
              className="reset-btn"
            >
              Update Password
            </Button>
          </Stack>

          <Group mt="xl" justify="center">
            <Anchor component={Link} to="/login" size="sm" c="dimmed" fw={600}>
              <Center inline>
                <IconArrowLeft size={14} stroke={2} />
                <Box ml={5}>Back to login</Box>
              </Center>
            </Anchor>
          </Group>
        </motion.div>
      </div>
    </div>
  );
};
export default ResetPassword;
